import type { GetPatientResponse } from '../../../../services/patientCard';
import { isValidEmail, isValidPhone } from '../../../../utils/contact';
import { CREATION_PHOTO_SLOT_IDS } from './photoSlots';
import type { SubmissionFiles } from './submissionFiles';

export type SubmissionStepId = 'patient' | 'treatment' | 'photos';

export interface StepProgress {
    id: SubmissionStepId;
    complete: boolean;
}

/** The subset of the patient card the completeness rules read. */
type SubmissionCard = Pick<
    GetPatientResponse,
    'firstName' | 'lastName' | 'email' | 'phone' | 'gender' | 'dateOfBirth' | 'practice'
> & {
    treatment?: Pick<
        NonNullable<GetPatientResponse['treatment']>,
        'caseType' | 'subCaseType' | 'manufacturer' | 'additionalInfo'
    >;
    monitoring?: Pick<NonNullable<GetPatientResponse['monitoring']>, 'iteroScanId'>;
};

// 'Select' is the dashboard's legacy "no manufacturer chosen" placeholder.
export const UNSET_MANUFACTURER = 'Select';

function isFilled(value: string | null | undefined): boolean {
    return (value ?? '').trim().length > 0;
}

function isFilledAndValid(
    value: string | null | undefined,
    isValid: (v: string) => boolean
): boolean {
    return isFilled(value) && isValid(value ?? '');
}

// Mirrors the Continue gate in dashboard patientDetails.tsx.
function isPatientDetailsComplete(card: SubmissionCard): boolean {
    return (
        isFilled(card.firstName) &&
        isFilled(card.lastName) &&
        isFilled(card.gender) &&
        isFilled(card.dateOfBirth) &&
        isFilledAndValid(card.phone, isValidPhone) &&
        isFilledAndValid(card.email, isValidEmail) &&
        isFilled(card.practice?.id)
    );
}

// Mirrors the case-type and approach gates plus the manufacturer and
// additional-information half of the dashboard "Review" step gate.
function isTreatmentPlanComplete(card: SubmissionCard): boolean {
    const t = card.treatment;
    const manufacturer = (t?.manufacturer ?? '').trim();
    return (
        isFilled(t?.caseType) &&
        isFilled(t?.subCaseType) &&
        manufacturer.length > 0 &&
        manufacturer !== UNSET_MANUFACTURER &&
        isFilled(t?.additionalInfo)
    );
}

// The dashboard "Review" step requires at least one radiograph or CBCT.
export function hasMedicalImages(files: SubmissionFiles): boolean {
    return files.radiographs.length > 0 || files.cbct.length > 0;
}

// The dashboard STL step accepts either uploaded STLs or a scan ID.
export function hasStls(card: SubmissionCard, files: SubmissionFiles): boolean {
    return files.stls.length > 0 || isFilled(card.monitoring?.iteroScanId);
}

// The dashboard labels all nine slots "Required Photos" but does not gate
// Continue on them, so this marks the step complete only when every slot is
// filled; it is informational and never blocked the practice's submission.
function areAllPhotoSlotsFilled(files: SubmissionFiles): boolean {
    return CREATION_PHOTO_SLOT_IDS.every((id) => files.photoSlots[id] !== null);
}

export function computeSubmissionProgress(
    card: SubmissionCard,
    files: SubmissionFiles
): readonly StepProgress[] {
    return [
        { id: 'patient', complete: isPatientDetailsComplete(card) },
        {
            id: 'treatment',
            complete:
                isTreatmentPlanComplete(card) && hasMedicalImages(files) && hasStls(card, files)
        },
        { id: 'photos', complete: areAllPhotoSlotsFilled(files) }
    ];
}
